/**
 * useAdminCosmetics — admin cosmetic-catalogue data layer (Wave 2 / v2.0a).
 *
 * Wraps the requireAdminScope-gated cosmetic endpoints on /api/cosmetics:
 *   - useAdminCosmetics()        GET    /api/cosmetics/admin  (full active set)
 *   - useCreateCosmeticItem()    POST   /api/cosmetics
 *   - useUpdateCosmeticItem()    PATCH  /api/cosmetics/:id
 *   - useArchiveCosmeticItem()   DELETE /api/cosmetics/:id
 *
 * Mirrors useChoreTemplates: the list query is disabled for non-admin sessions
 * and mutations throw the shared `ApiError` so <AdminReauthModal> can detect a
 * 403 admin-scope error (isAdminScopeError) and retry after a fresh re-auth.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CosmeticKind } from '@stell/shared';
import { toApiError } from './useMembers';
import { useSession } from '../auth/useSession';

export interface CosmeticAdminRow {
  id: string;
  kind: CosmeticKind;
  name: string;
  description: string | null;
  asset_key: string;
  point_cost: number;
  min_age: number;
  active: number;
  created_by: string;
  created_at: string;
}

export interface CosmeticItemInput {
  kind: CosmeticKind;
  name: string;
  description?: string;
  asset_key: string;
  point_cost: number;
  min_age?: number;
}

export const ADMIN_COSMETICS_KEY = ['cosmetics', 'admin'] as const;

export function useAdminCosmetics() {
  const { data: session } = useSession();
  const isAdmin = session?.member?.role === 'admin';
  return useQuery<CosmeticAdminRow[]>({
    queryKey: ADMIN_COSMETICS_KEY,
    enabled: isAdmin,
    queryFn: async () => {
      const res = await fetch('/api/cosmetics/admin', { credentials: 'same-origin' });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as CosmeticAdminRow[];
    },
  });
}

export function useCreateCosmeticItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CosmeticItemInput) => {
      const res = await fetch('/api/cosmetics', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as CosmeticAdminRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics'] });
    },
  });
}

export function useUpdateCosmeticItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CosmeticItemInput }) => {
      const res = await fetch(`/api/cosmetics/${id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as CosmeticAdminRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics'] });
    },
  });
}

export function useArchiveCosmeticItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/cosmetics/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });
      if (!res.ok) throw await toApiError(res);
      return (await res.json()) as CosmeticAdminRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics'] });
    },
  });
}
